import React from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/base';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/base/Accordion';
import { Card, CardContent } from '@/components/base/Card';
import { HelpCenter, NoteAlt, PhotoFilter, Science, Sparkle } from '@/assets/icons';
import { cn } from '@/lib/utils';
import { useScale } from '@/lib/hooks/useScale';
import { SOAPPreview, type SOAPData } from '@/components/consultation/SOAPPreview';
import { PatientInformationCard } from '@/components/consultation/paData/PatientInformationCard';
import { AyurvedicAssessmentCard } from '@/components/consultation/paData/AyurvedicAssessmentCard';
import { AITranscribedDataCard } from '@/components/consultation/paData/AITranscribedDataCard';
import { RegistrationCard } from '@/components/consultation/paData/RegistrationCard';
import { GeneralExaminationCard } from '@/components/consultation/paData/GeneralExaminationCard';
import { EnvironmentalProfileCard } from '@/components/consultation/paData/EnvironmentalProfileCard';
import { PranikAIPanel } from '@/components/consultation/paData/PranikAIPanel';
import { ConsultationScrollArea } from '@/components/consultation/ConsultationScrollArea';
import type {
  AyurvedaData,
  VitalSigns,
  ClinicalAssessmentEntry,
  ClinicalAssessmentParam,
} from '@/lib/types/consultation';
import { formatInvestigationEntry, type InvestigationEntry } from '@/lib/utils/clinicalText';
import type { PersonalHistory } from '@/ayurveda/types/ayurveda';

// `commonly_eaten_food` (CDSS-extracted list of foods the patient regularly
// eats) streams in as its own field, separate from `personal_history` — the
// backend's personal_history delta never carries a `diet` key at all. Map it
// into Diet whenever Diet itself is still empty, so it shows up live during
// the consultation too, not just in the Case Sheet (which already does this
// same mapping when building its own Personal History).
function mergeCommonlyEatenFoodIntoDiet(
  personalHistory: PersonalHistory | null | undefined,
  commonlyEatenFood: string[] | undefined
): PersonalHistory | null | undefined {
  if (personalHistory?.diet || !commonlyEatenFood || commonlyEatenFood.length === 0) {
    return personalHistory;
  }
  return {
    appetite: null,
    sleep: null,
    exercise: null,
    bowel: null,
    bladder: null,
    addiction: null,
    menstrual: null,
    ...personalHistory,
    diet: commonlyEatenFood.join(', '),
  };
}

export type ConsultationViewProps = {
  header: {
    title: string;
    patientName: string;
    patientIdentifier: string;
    status: 'completed' | 'in-progress' | 'draft';
  };
  sessionMode?: boolean;
  actions: {
    onBack: () => void;
    onComplete?: () => void;
    completeDisabled?: boolean;
    completePending?: boolean;
    onSaveDraft?: () => void;
  };
  patient?: {
    name: string;
    identifier: string;
    age?: number;
    gender?: string;
    birthDate?: string;
  };
  /** Vitals data to display (fetched from Encounter) */
  vitals?: VitalSigns | null;
  /** Patient FHIR ID — used for lab report upload/fetch */
  patientId?: string;
  leftPanel: {
    audioRecorder: React.ReactNode;
    vitalsForm: React.ReactNode;
    soapData?: SOAPData | null;
    soapLoading?: boolean;
    ayurvedaData?: AyurvedaData | null;
    /** Astha Sthaana Pareeksha/Prakruthi/Dosha/Aama/Diagnosis/Treatment chip
     *  values, live from the CDSS agent's clinical_assessment stream. */
    clinicalAssessment?: ClinicalAssessmentEntry[];
    onClinicalAssessmentOverride?: (parameter: ClinicalAssessmentParam, value: string) => void;
    pendingClinicalAssessmentParameter?: ClinicalAssessmentParam | null;
  };
  middle: {
    suggestions: string[];
    investigations: (string | InvestigationEntry)[];
    diagnoses: string[];
    diffDiagnoses: string[];
  };
  right: {
    symptoms: string[];
    doctorInvestigations: string[];
    prescribedMedications: string[];
    suggestedMedications?: string[];
    dietRecommendations: string[];
    lifestyleRecommendations: string[];
    viruddhaAhaara: string[];
    referenceSlokas?: import('@/lib/types/consultation').ReferenceSlokaItem[];
  };
  isAyurveda?: boolean;
};

export function ConsultationView(props: ConsultationViewProps) {
  if (props.isAyurveda) {
    return <AyurvedaConsultationView {...props} />;
  }
  return <LegacyConsultationView {...props} />;
}

/**
 * 3-column Ayurveda "Physician Assistant Data Preview" layout, matching the
 * Figma "Summary" frame (fileKey wSRMiYYGgVmi3JQLP0l5TS, node 5378:9774):
 * left column (Pranik AI / Diagnosis / Treatment), middle column (header,
 * Patient Information, Ayurvedic Assessment, audio bar), right column
 * (Registration, General Examination, Environmental Profile).
 */
function AyurvedaConsultationView({ vitals, leftPanel, middle, right }: ConsultationViewProps) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1 gap-2 py-2">
        <ResizablePanel defaultSize={26} minSize={18} className="p-4">
          <div className="flex h-full w-full flex-col gap-2">
            {/* Two independently-scrolling cards, matching Figma's separate
                "Pranik's CDSS" and "AI Transcribed data" frames. Each card
                component owns its own bg-white rounded-3xl box AND its own
                internal scroll area, so the scrollbar clips to the card's
                rounded corners instead of floating outside it. */}
            <div className="min-h-0 flex-[0_0_37%]">
              <PranikAIPanel diagnoses={middle.diagnoses} referenceSlokas={right.referenceSlokas} />
            </div>
            <div className="min-h-0 flex-[0_0_63%]">
              <AITranscribedDataCard entries={leftPanel.clinicalAssessment ?? []} />
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={45} minSize={30} className="p-4">
          <div className="flex h-full w-full flex-col gap-2">
            {/* Card box — matches Figma's "card-body" frame. The audio bar +
                "Powered by Pranik.AI" line below sit outside this box, directly
                on the page background, per Figma's separate "Frame 43124". */}
            <div className="glass-panel-blur flex min-h-0 flex-1 flex-col rounded-3xl">
              <ConsultationScrollArea className="w-full flex-1 px-4 py-2">
                <div className="mb-4 flex flex-col gap-1 rounded-2xl border border-white bg-[#F3EBFF] px-4 py-2">
                  <h1 className="font-serif text-[calc(20px*var(--app-scale))] font-medium leading-[30px] text-[#3C4F1E]">
                    AI Transcribed Summary
                  </h1>
                  <p className="font-hanken text-sm font-medium tracking-[-0.16px] text-[#8D90A1]">
                    Review the kiosk findings and provisional assessment.
                  </p>
                </div>

                <PatientInformationCard
                  chiefComplaint={leftPanel.ayurvedaData?.chiefComplaint}
                  duration={leftPanel.ayurvedaData?.duration}
                  modeOfOnset={leftPanel.ayurvedaData?.modeOfOnset}
                  history={leftPanel.ayurvedaData?.history}
                  pastIllness={leftPanel.ayurvedaData?.pastIllness}
                  treatmentHistory={leftPanel.ayurvedaData?.treatmentHistory}
                  historyFamilyHistory={leftPanel.ayurvedaData?.historyFamilyHistory}
                  currentMedication={leftPanel.ayurvedaData?.currentMedication}
                  familySocialOccupational={leftPanel.ayurvedaData?.familySocialOccupational}
                  personalHistory={mergeCommonlyEatenFoodIntoDiet(
                    leftPanel.ayurvedaData?.personalHistory,
                    leftPanel.ayurvedaData?.commonlyEatenFood
                  )}
                />
                <AyurvedicAssessmentCard
                  entries={leftPanel.clinicalAssessment ?? []}
                  onOverride={leftPanel.onClinicalAssessmentOverride}
                  pendingParameter={leftPanel.pendingClinicalAssessmentParameter}
                />
              </ConsultationScrollArea>
            </div>

            <div className="shrink-0 px-2">{leftPanel.audioRecorder}</div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={24} minSize={18} className="p-4">
          {/* Each card owns its own internal scroll area, matching the left
              column, so the scrollbar thumb belongs to a specific card
              instead of floating in the shared gutter across all three. */}
          <div className="flex h-full w-full flex-col gap-2">
            <RegistrationCard registration={leftPanel.ayurvedaData?.registration} />
            <div className="min-h-0 flex-1">
              <GeneralExaminationCard vitals={vitals} />
            </div>
            <div className="min-h-0 flex-1">
              <EnvironmentalProfileCard desaInformation={leftPanel.ayurvedaData?.desaInformation} />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
      <p className="shrink-0 pb-1 pt-2 text-center font-hanken text-sm font-medium tracking-[-0.16px] text-[#4D68B9]">
        Powered by Pranik.AI
      </p>
    </div>
  );
}

/**
 * Pre-existing 2-panel layout, kept unchanged for non-Ayurveda departments.
 */
function LegacyConsultationView({ vitals, leftPanel, middle, right }: ConsultationViewProps) {
  const scale = useScale();
  const showCdssPanel = true; // Always show for both Pranik HMIS and Swecha
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <ResizablePanelGroup direction="horizontal" className="gap-2 py-2">
        <ResizablePanel defaultSize={65} minSize={30} className="glass-panel-blur rounded-3xl p-4">
          <div className="flex h-full w-full flex-col">
            {/* Scrollable content area with padding-bottom for floating panel */}
            <div className="scrollbar-thin flex-1 overflow-y-auto px-4 py-2 pb-[140px]">
              {/* Header Title */}
              <div className="mb-6">
                <h1 className="font-serif text-[calc(26px*var(--app-scale))] font-medium leading-[calc(32px*var(--app-scale))] tracking-[-0.15px] text-[#1F3959]">
                  Physician Assistant Data Preview
                </h1>
                <p className="font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#8D90A1]">
                  Structured clinical data submitted by the Kiosk is displayed below. Please verify
                  the PA's objective findings and provisional assessment.
                </p>
              </div>

              {/* Left panel content: same SOAP preview for every department */}
              <SOAPPreview
                soapData={leftPanel.soapData ?? null}
                isLoading={leftPanel.soapLoading}
                vitals={vitals}
              />
            </div>

            {/* Floating audio panel at bottom */}
            <div className="sticky bottom-0 z-10 px-4 pb-2 pt-2">{leftPanel.audioRecorder}</div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={35} className="glass-panel-blur rounded-3xl p-4">
          <div className="scrollbar-thin h-full w-full overflow-y-auto px-1 py-1">
            {showCdssPanel && (
              <Card
                padding="none"
                className="mb-4 rounded-[20px] border-[#fbfafd] bg-[rgba(253,252,250,0.5)] backdrop-blur-[15px]"
              >
                <CardContent className="flex flex-col gap-4 p-5">
                  <div className="rounded-lg border border-[#eaebf3] bg-gradient-to-r from-[#fff4c2] via-[#f0f3fe] to-[#d1dbfd] p-1">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center">
                        <Sparkle size={18 * scale} className="text-[#1f3b73]" />
                      </div>
                      <h2 className="font-serif text-[calc(22px*var(--app-scale))] leading-[calc(30px*var(--app-scale))] tracking-[0px] text-[#253057]">
                        Pranik's CDSS
                      </h2>
                    </div>
                    <p className="pl-8 font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#585859]">
                      This AI-powered clinical decision support system offers guidance, but please
                      validate the results before use.
                    </p>
                  </div>
                  <Accordion
                    type="multiple"
                    className="w-full"
                    defaultValue={[
                      'suggestions',
                      'relevant-investigations',
                      'differential-diagnosis',
                    ]}
                  >
                    <AccordionItem
                      value="suggestions"
                      className={cn(
                        'rounded-xl transition-all',
                        'data-[state=open]:glass-panel-blur data-[state=open]:mb-2'
                      )}
                    >
                      <AccordionTrigger>
                        <div className="flex items-center gap-2 text-left font-hanken text-[calc(22px*var(--app-scale))] font-medium leading-[calc(26px*var(--app-scale))] tracking-[0px] text-[#42434B]">
                          <HelpCenter size={20 * scale} fill="#061A61" />
                          <span>Suggested questions</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {middle.suggestions && middle.suggestions.length > 0 ? (
                          <ul className="list-decimal space-y-2 pl-5">
                            {middle.suggestions.map((s, idx) => (
                              <li
                                key={`sugg-${idx}`}
                                className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(28px*var(--app-scale))] tracking-[0px] text-[#585859]"
                              >
                                {s}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[calc(16px*var(--app-scale))] text-muted-foreground">
                            No items yet
                          </p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem
                      value="relevant-investigations"
                      className={cn(
                        'rounded-xl transition-all',
                        'data-[state=open]:glass-panel-blur data-[state=open]:mb-2'
                      )}
                    >
                      <AccordionTrigger>
                        <div className="flex items-center gap-2 text-left font-hanken text-[calc(22px*var(--app-scale))] font-medium leading-[calc(26px*var(--app-scale))] tracking-[0px] text-[#42434B]">
                          <PhotoFilter size={20 * scale} fill="#061A61" />
                          <span>Relevant Investigations</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {middle.investigations && middle.investigations.length > 0 ? (
                          <ul className="list-decimal space-y-2 pl-5">
                            {middle.investigations.map((inv, idx) => (
                              <li
                                key={`inv-${idx}`}
                                className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(28px*var(--app-scale))] tracking-[0px] text-[#585859]"
                              >
                                {typeof inv === 'string' ? inv : formatInvestigationEntry(inv)}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[calc(16px*var(--app-scale))] text-muted-foreground">
                            No items yet
                          </p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem
                      value="differential-diagnosis"
                      className={cn(
                        'rounded-xl transition-all',
                        'data-[state=open]:glass-panel-blur data-[state=open]:mb-2'
                      )}
                    >
                      <AccordionTrigger>
                        <div className="flex items-center gap-2 text-left font-hanken text-[calc(22px*var(--app-scale))] font-medium leading-[calc(26px*var(--app-scale))] tracking-[0px] text-[#42434B]">
                          <Science size={20 * scale} fill="#061A61" />
                          <span>Suggestive Differentials</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {middle.diagnoses && middle.diagnoses.length > 0 ? (
                          <ul className="list-decimal space-y-2 pl-5">
                            {middle.diagnoses.map((diagnosis, idx) => (
                              <li
                                key={`diff-diag-${idx}`}
                                className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(28px*var(--app-scale))] tracking-[0px] text-[#585859]"
                              >
                                {diagnosis}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[calc(16px*var(--app-scale))] text-muted-foreground">
                            No items yet
                          </p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            )}

            <Card
              padding="none"
              className="mb-4 rounded-[20px] border-[#fbfafd] bg-[rgba(253,252,250,0.5)] backdrop-blur-[15px]"
            >
              <CardContent className="flex flex-col gap-4 p-5">
                <Accordion type="multiple" className="w-full" defaultValue={['symptoms']}>
                  <div className="mb-4">
                    <div className="rounded-lg border border-[#eaebf3] bg-gradient-to-r from-[#fff4c2] via-[#f0f3fe] to-[#d1dbfd] p-1">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center">
                          <Sparkle size={18 * scale} className="text-[#1f3b73]" />
                        </div>
                        <h2 className="font-serif text-[calc(22px*var(--app-scale))] leading-[calc(30px*var(--app-scale))] tracking-[0px] text-[#253057]">
                          AI Transcribed Summary
                        </h2>
                      </div>
                      <p className="pl-8 font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(22px*var(--app-scale))] tracking-[-0.16px] text-[#585859]">
                        This is AI live transcription from the conversation
                      </p>
                    </div>
                  </div>
                  <AccordionItem
                    value="symptoms"
                    className={cn(
                      'rounded-xl transition-all',
                      'data-[state=open]:glass-panel-blur data-[state=open]:mb-2'
                    )}
                  >
                    <AccordionTrigger>
                      <div className="flex items-center gap-2 text-left font-hanken text-[calc(22px*var(--app-scale))] font-medium leading-[calc(26px*var(--app-scale))] tracking-[0px] text-[#42434B]">
                        <NoteAlt size={20 * scale} fill="#1F3959" />
                        <span>Symptoms</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {right.symptoms && right.symptoms.length > 0 ? (
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(28px*var(--app-scale))] tracking-[0px] text-[#585859]">
                          {right.symptoms.join(', ')}
                        </p>
                      ) : (
                        <p className="text-[calc(16px*var(--app-scale))] text-muted-foreground">
                          No items yet
                        </p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
